// Project import (Unit 27a frontend). Triggered by the `[↥]` header button
// (Layout.tsx). Shows a file picker → progress modal → success or one of
// the SC-14 validation modals coming back from the server.

import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";

import { apiFetch } from "../api/client";

import "./ProjectImport.css";

const PROJECTS_KEY = "/api/projects/?archived=0";
const EXPANDED_STORAGE_KEY = "ophelia.tree.expanded";

interface ImportResponse {
  ok: boolean;
  project_id: number;
  project_name: string;
  paper_count: number;
  annotation_count: number;
}

export function ProjectImport() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<null | string>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutate } = useSWRConfig();
  const navigate = useNavigate();

  function pick() {
    if (busy) return;
    setError(null);
    fileInputRef.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-picking the same file later
    if (!file) return;

    setBusy(true);
    setProgress(`Uploading ${file.name}…`);
    setError(null);

    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch("/api/projects/import", {
        method: "POST",
        body: fd,
        headers: {},  // let the browser set multipart boundary
      });
      if (!res.ok) {
        const body = await safeJson(res);
        const detail =
          (body as { detail?: string } | null)?.detail ??
          `Import failed (${res.status}).`;
        throw new Error(detail);
      }
      const result = (await res.json()) as ImportResponse;

      // Pre-flag the new project as expanded so ProjectTree picks it up.
      try {
        const raw = window.localStorage.getItem(EXPANDED_STORAGE_KEY);
        const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
        map[String(result.project_id)] = true;
        window.localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(map));
      } catch {
        /* localStorage failure is non-fatal */
      }

      setProgress(
        `Imported ${result.paper_count} paper(s), ${result.annotation_count} annotation(s).`,
      );
      void mutate(PROJECTS_KEY);
      // Brief success flash, then navigate to the new project.
      window.setTimeout(() => {
        navigate(`/?project=${result.project_id}`);
        setBusy(false);
        setProgress(null);
      }, 800);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
      setProgress(null);
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".ophelia,application/zip"
        onChange={onFile}
        style={{ display: "none" }}
      />
      <button
        type="button"
        className="app-header__btn app-header__btn--outline"
        onClick={pick}
        disabled={busy}
        aria-label="Import project archive"
      >
        ↥<span className="app-header__btn-label"> Import</span>
      </button>

      {(progress || error) && (
        <div
          className="import-toast"
          role="status"
          onClick={() => {
            if (error) setError(null);
          }}
        >
          {progress && <span>{progress}</span>}
          {error && (
            <>
              <strong>Import failed.</strong>
              <p>{error}</p>
              <p className="import-toast__hint">Click to dismiss.</p>
            </>
          )}
        </div>
      )}
    </>
  );
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}
